import { useCallback, useSyncExternalStore } from 'react';

export type AnsweringMode = 'focus' | 'list';

const STORAGE_KEY = 'probability-event:answering-mode';
const DEFAULT_MODE: AnsweringMode = 'focus';

function readStoredMode(): AnsweringMode {
	if (typeof window === 'undefined') return DEFAULT_MODE;
	const stored = window.localStorage.getItem(STORAGE_KEY);
	return stored === 'list' || stored === 'focus' ? stored : DEFAULT_MODE;
}

let currentMode: AnsweringMode = readStoredMode();
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

function getSnapshot(): AnsweringMode {
	return currentMode;
}

function setStoredMode(mode: AnsweringMode) {
	if (mode === currentMode) return;
	currentMode = mode;
	if (typeof window !== 'undefined') {
		window.localStorage.setItem(STORAGE_KEY, mode);
	}
	listeners.forEach((listener) => listener());
}

export function useAnsweringMode() {
	// Server/hydrate snapshot stays on DEFAULT so Layout pin doesn't mismatch SSR.
	const mode = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_MODE);
	const setMode = useCallback((next: AnsweringMode) => setStoredMode(next), []);

	return { mode, setMode };
}
